import type { TwiageCase } from "@/App/types/twiageTypes";

import type {
  Filter,
  DateRangeFilter,
  ComparisonFilter,
  InFilter,
  FilterSet,
} from "@/Pages/Cases/filtering/filters/CaseFilter";
import { getValueByPath } from "@/App/utils/getValueByPath";

export enum Operator {
  OR = "OR",
}

export const filterCases = ({
  twiageCases,
  filters,
}: {
  twiageCases: TwiageCase[];
  filters: Filter[];
}): TwiageCase[] => {
  return twiageCases.filter((twiageCase) => filterCase({ twiageCase, filters }));
};

export const filterCase = ({ twiageCase, filters }: { twiageCase: TwiageCase; filters: Filter[] }): boolean => {
  if (!filters.length || filters.length === 0) {
    return true;
  }
  return filters.every((filter) => {
    const { filters, operator } = filter as FilterSet;

    if (filters?.length) {
      if (operator === Operator.OR) {
        return filters.some((filter) => satisfyFilter({ twiageCase, filter }) === Result.PASSED);
      }
      for (const filter of filters) {
        switch (satisfyFilter({ twiageCase, filter })) {
          case Result.PASSED:
            if (filter.primary) {
              return true;
            }
            break;
          case Result.REJECTED:
            return false;
        }
      }
      return true;
    }

    return satisfyFilter({ twiageCase, filter }) === Result.PASSED;
  });
};

enum Result {
  PASSED,
  SKIPPED,
  REJECTED,
}

const satisfyFilter = ({ twiageCase, filter }: { twiageCase: TwiageCase; filter: Filter }): Result => {
  const boolToResult = (bool: boolean): Result => (bool ? Result.PASSED : Result.REJECTED);

  const value = getValueByPath({ object: twiageCase, path: filter.fieldName });
  if (!value && filter.skipIfNoValue) {
    return Result.SKIPPED;
  }

  if ("value" in filter) {
    const valueToCompare = (filter as ComparisonFilter).value;
    if ((filter as ComparisonFilter).negative) {
      return boolToResult(value !== valueToCompare);
    } else {
      return boolToResult(value === valueToCompare);
    }
  }

  const arrayToCheckAgainst = (filter as InFilter).values;
  if (arrayToCheckAgainst?.length === 0) return Result.PASSED;

  if (arrayToCheckAgainst) {
    return boolToResult(arrayToCheckAgainst.includes(value));
  }

  const { from, to } = filter as DateRangeFilter;
  if (from || to) {
    const dateField = new Date(value as string).getTime();
    if (from && to) {
      return boolToResult(new Date(from).getTime() <= dateField && new Date(to).getTime() > dateField);
    } else if (from) {
      return boolToResult(new Date(from).getTime() <= dateField);
    } else if (to) {
      return boolToResult(new Date(to).getTime() > dateField);
    }
  }
  console.error("unknown case filter");
  return Result.PASSED;
};
